/**
 * Iter 107 · Admin Broadcast Control Panel.
 *
 * Ultra-premium glassmorphic web form that pushes real-time pop-ups
 * to every kiosk client subscribed on `/api/ws/updates`. Delivers the
 * payload via `POST /api/admin/broadcast-update` with the required
 * `X-Admin-Key` header.
 *
 * Route:   /admin/broadcast
 * Tenant:  MASTER_JOHN + any explicit tenant_id typed into the target.
 */

import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ADMIN_KEY = "john-kiosk-2026";

const LEVELS = [
  { value: "info", label: "ℹ  INFO", ring: "from-sky-400 to-cyan-500" },
  { value: "warning", label: "⚠  WARNING", ring: "from-amber-400 to-orange-500" },
  { value: "critical", label: "🚨 CRITICAL", ring: "from-rose-500 to-red-600" },
];

export default function AdminBroadcast() {
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [level, setLevel] = useState("info");
  const [tenantId, setTenantId] = useState("MASTER_JOHN");
  const [sending, setSending] = useState(false);
  const [lastReceipt, setLastReceipt] = useState(null);

  const canSend =
    title.trim().length > 0 && message.trim().length > 0 && !sending;

  async function handleBroadcast() {
    if (!canSend) return;
    setSending(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/broadcast-update`,
        {
          title: title.trim(),
          message: message.trim(),
          level,
          tenant_id: tenantId.trim() || "ALL",
        },
        { headers: { "X-Admin-Key": ADMIN_KEY, "Content-Type": "application/json" } },
      );
      setLastReceipt(res.data);
      toast.success(
        `⚡ Broadcast delivered · reached ${res.data.reached}/${res.data.connected} clients`,
      );
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        "broadcast failed";
      toast.error(`Broadcast failed: ${detail}`);
      setLastReceipt({ ok: false, error: detail });
    } finally {
      setSending(false);
    }
  }

  const levelMeta = LEVELS.find((l) => l.value === level) || LEVELS[0];

  return (
    <div
      className="relative min-h-screen w-full overflow-hidden bg-slate-950 text-slate-100"
      data-testid="admin-broadcast-page"
    >
      {/* Ambient glow layers */}
      <div className="pointer-events-none absolute -top-24 -left-24 h-96 w-96 rounded-full bg-emerald-500/20 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-32 -right-24 h-[28rem] w-[28rem] rounded-full bg-sky-500/20 blur-3xl" />
      <div className="pointer-events-none absolute top-1/3 left-1/2 h-72 w-72 -translate-x-1/2 rounded-full bg-fuchsia-500/10 blur-3xl" />

      <div className="relative z-10 mx-auto flex max-w-3xl flex-col gap-8 px-6 py-16">
        <header className="flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.35em] text-emerald-300/80">
              Iter 107 · Real-Time Ops
            </p>
            <h1 className="mt-2 text-4xl font-black leading-tight text-white sm:text-5xl">
              Admin Broadcast Control Panel
            </h1>
            <p className="mt-3 text-sm text-slate-400">
              Push a pop-up notification live to every kiosk currently
              subscribed to <code className="text-emerald-300">/api/ws/updates</code>.
            </p>
          </div>
        </header>

        <section
          className="rounded-3xl border border-white/10 bg-white/5 p-8 shadow-2xl backdrop-blur-xl"
          data-testid="admin-broadcast-card"
        >
          <div className="space-y-6">
            {/* Title */}
            <div>
              <label
                className="mb-2 block text-xs font-semibold uppercase tracking-widest text-slate-300"
                htmlFor="ab-title"
              >
                Title / Τίτλος
              </label>
              <input
                id="ab-title"
                data-testid="ab-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Cheque Deadline Reminder"
                className="w-full rounded-xl border border-white/10 bg-slate-900/50 px-4 py-3 text-base text-white placeholder:text-slate-500 outline-none transition focus:border-emerald-400 focus:ring-2 focus:ring-emerald-400/40"
              />
            </div>

            {/* Message */}
            <div>
              <label
                className="mb-2 block text-xs font-semibold uppercase tracking-widest text-slate-300"
                htmlFor="ab-message"
              >
                Message / Μήνυμα
              </label>
              <textarea
                id="ab-message"
                data-testid="ab-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={5}
                placeholder="Body text broadcast to every connected kiosk…"
                className="w-full resize-none rounded-xl border border-white/10 bg-slate-900/50 px-4 py-3 text-base text-white placeholder:text-slate-500 outline-none transition focus:border-emerald-400 focus:ring-2 focus:ring-emerald-400/40"
              />
            </div>

            {/* Level + Tenant */}
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <label className="mb-2 block text-xs font-semibold uppercase tracking-widest text-slate-300">
                  Level / Επίπεδο
                </label>
                <div
                  className="grid grid-cols-3 gap-2"
                  data-testid="ab-level-group"
                >
                  {LEVELS.map((l) => (
                    <button
                      key={l.value}
                      type="button"
                      data-testid={`ab-level-${l.value}`}
                      onClick={() => setLevel(l.value)}
                      className={`rounded-lg border px-2 py-2 text-xs font-bold transition ${
                        level === l.value
                          ? `border-transparent bg-gradient-to-br ${l.ring} text-slate-950 shadow-lg`
                          : "border-white/10 bg-white/5 text-slate-300 hover:bg-white/10"
                      }`}
                    >
                      {l.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label
                  className="mb-2 block text-xs font-semibold uppercase tracking-widest text-slate-300"
                  htmlFor="ab-tenant"
                >
                  Tenant ID
                </label>
                <input
                  id="ab-tenant"
                  data-testid="ab-tenant"
                  value={tenantId}
                  onChange={(e) => setTenantId(e.target.value)}
                  placeholder="MASTER_JOHN or ALL"
                  className="w-full rounded-xl border border-white/10 bg-slate-900/50 px-4 py-3 text-base text-white placeholder:text-slate-500 outline-none transition focus:border-emerald-400 focus:ring-2 focus:ring-emerald-400/40"
                />
                <p className="mt-1 text-[11px] text-slate-500">
                  Type <code className="text-emerald-300">ALL</code> to broadcast globally.
                </p>
              </div>
            </div>

            {/* Broadcast button */}
            <button
              type="button"
              data-testid="ab-broadcast-btn"
              disabled={!canSend}
              onClick={handleBroadcast}
              className={`group relative w-full overflow-hidden rounded-2xl border border-emerald-400/40 bg-gradient-to-br from-emerald-500 via-emerald-400 to-lime-400 py-5 text-lg font-black tracking-wide text-slate-950 shadow-[0_0_35px_rgba(16,185,129,0.55)] transition ${
                canSend
                  ? "hover:shadow-[0_0_55px_rgba(16,185,129,0.9)] hover:brightness-110"
                  : "cursor-not-allowed opacity-50"
              }`}
            >
              <span className="relative z-10">
                {sending
                  ? "…broadcasting"
                  : "⚡ ΕΚΠΟΜΠΗ POP-UP / BROADCAST NOW"}
              </span>
              <span className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/50 to-transparent transition group-hover:translate-x-full" />
            </button>

            {/* Preview / receipt */}
            {lastReceipt && (
              <div
                data-testid="ab-last-receipt"
                className="rounded-xl border border-white/10 bg-black/30 p-4 font-mono text-xs text-emerald-200"
              >
                <pre className="whitespace-pre-wrap break-words">
                  {JSON.stringify(lastReceipt, null, 2)}
                </pre>
              </div>
            )}
          </div>
        </section>

        <footer className="text-center text-xs text-slate-500">
          Made by John · Iter 107 real-time broadcast rail ·{" "}
          <span className={`bg-gradient-to-r ${levelMeta.ring} bg-clip-text text-transparent`}>
            {levelMeta.label}
          </span>
        </footer>
      </div>
    </div>
  );
}
